// What a conversation row is called when nobody said who they were.
//
// Roughly six chat visitors in a hundred identify themselves, so the fallback is
// the COMMON case on this modality and must not read as missing data — no
// "unknown", no "(no name)", no empty cell. A short stable tag derived from the
// conversation id gives every row a name a reader can refer to and search for.
//
// Extracted from the two page components so the rule exists once and can be
// tested at the exact line where it would regress. The bug it guards against is
// specific: `visitor_name` arriving as the NUMBER 0, which `name || fallback`
// handles correctly but `name && <span>{name}</span>` renders as the string "0"
// — a falsy-number JSX guard. Every value below that is falsy, blank, or
// whitespace-only must reach the fallback.

/** Four stable hex characters derived from an id. Never empty. */
export const shortTag = (id) => {
  const s = String(id || "");
  if (!s) return "0000";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return (h >>> 0).toString(16).toUpperCase().slice(-4).padStart(4, "0");
};

export const fallbackGuestLabel = (id) => `Guest ${shortTag(id)}`;

/**
 * The label for a conversation row.
 *
 * @param {*} name the stored visitor name — may be null, "", "  ", or 0
 * @param {*} id the conversation id, used for the fallback tag
 */
export const displayNameFor = (name, id) => {
  // The falsy test comes FIRST and is the whole point: `String(0).trim()` is
  // "0", which is truthy, so a null-check alone hands the number 0 straight
  // through as a name. That is exactly the bug this helper exists to stop.
  // "0" as a string is the same artifact one JSON round-trip later, and nobody
  // is called it, so it goes to the fallback too.
  const trimmed = name ? String(name).trim() : "";
  if (!trimmed || trimmed === "0") return fallbackGuestLabel(id);
  return trimmed;
};
